import { useState } from "react";
import { Link } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { ConcurrencyMetric } from "@starsach/shared-types";
import { useConcurrency } from "../../api/hooks";
import { Card, ErrorState, LoadingState, PollEmptyState, inputClass } from "../../components/ui";
import { useUiStore, type ConcurrencyChannel } from "../../store/ui";
import { useFormat } from "../../lib/useFormat";

const LABELS: Record<ConcurrencyMetric, string> = {
  ConcurrentCalls: "Concurrent calls",
  ConcurrentCallsPercentage: "Calls, % of quota",
  ConcurrentActiveChats: "Concurrent active chats",
  ConcurrentActiveChatsPercentage: "Chats, % of quota",
  ConcurrentTasks: "Concurrent tasks",
  ConcurrentTasksPercentage: "Tasks, % of quota",
  ConcurrentEmails: "Concurrent emails",
  ConcurrentEmailsPercentage: "Emails, % of quota",
};

/** Voice first, then chats, then email; tasks last. Each channel is a count and a percentage of its quota. */
const CHANNELS: Array<{ id: ConcurrencyChannel; title: string; metrics: ConcurrencyMetric[] }> = [
  { id: "voice", title: "Voice", metrics: ["ConcurrentCalls", "ConcurrentCallsPercentage"] },
  { id: "chats", title: "Chats", metrics: ["ConcurrentActiveChats", "ConcurrentActiveChatsPercentage"] },
  { id: "email", title: "Email", metrics: ["ConcurrentEmails", "ConcurrentEmailsPercentage"] },
  { id: "tasks", title: "Tasks", metrics: ["ConcurrentTasks", "ConcurrentTasksPercentage"] },
];

/** ADR-0004: pushed metric snapshots, drawn here - no iframe, no shared dashboard link. */
export function ConcurrencyTab({ environmentId, connectedAt }: { environmentId: string; connectedAt: string | undefined }) {
  const fmt = useFormat();
  const [hours, setHours] = useState(24);
  const q = useConcurrency(environmentId, hours);
  const minimised = useUiStore((s) => s.concurrencyMinimised);
  const toggle = useUiStore((s) => s.toggleConcurrencyChannel);
  const byMetric = new Map((q.data?.series ?? []).map((s) => [s.metric, s]));

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Section 8.9: the increase is asked for from here, beside the line that is near its quota. */}
        <Link to={`/quotas/${environmentId}`} className="text-sm underline">Request a quota increase</Link>
        <span className="flex items-center gap-2">
        <label htmlFor="concurrency-hours" className="text-sm">
          Window
        </label>
        <select id="concurrency-hours" value={hours} onChange={(e) => setHours(Number(e.target.value))} className={`${inputClass} mt-0 w-auto`}>
          <option value={6}>6 hours</option>
          <option value={24}>24 hours</option>
          <option value={72}>3 days</option>
          <option value={168}>7 days</option>
        </select>
        </span>
      </div>
      {q.isPending && <LoadingState />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data &&
        CHANNELS.map((channel) => {
          const series = channel.metrics.map((m) => byMetric.get(m)).filter((s): s is NonNullable<typeof s> => Boolean(s));
          const hidden = minimised.includes(channel.id);
          const controlId = `concurrency-${channel.id}`;
          return (
            <Card key={channel.id} className="p-0">
              {/* The card's own title slot is skipped so the heading and its control share one row. */}
              <div className="flex items-center justify-between gap-3 px-6 py-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  {channel.title}
                  {series.length === 0 && <span className="ml-2 font-normal normal-case tracking-normal"> no data in this window</span>}
                </h2>
                <button type="button" aria-expanded={!hidden} aria-controls={controlId} onClick={() => toggle(channel.id)} className="text-xs font-medium text-neutral-600 hover:text-ink dark:text-neutral-400 dark:hover:text-white">
                  {hidden ? "Show" : "Minimize"}
                </button>
              </div>
              {!hidden && (
                <div id={controlId} className="px-6 pb-6">
                  {series.length === 0 ? (
                    <PollEmptyState connectedAt={connectedAt} what="concurrency" title={`No ${channel.title.toLowerCase()} concurrency data yet`} hint="Points arrive with the quota poller, when the stack is deployed and every six hours after; a channel the instance does not use never reports." />
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2">
                      {series.map((s) => (
                        <figure key={s.metric}>
                          <figcaption className="mb-1 text-sm font-medium">{LABELS[s.metric as ConcurrencyMetric] ?? s.metric}</figcaption>
                          <div className="h-48" role="img" aria-label={`${LABELS[s.metric as ConcurrencyMetric] ?? s.metric}, ${s.points.length} points, latest ${s.points.at(-1)?.v ?? "n/a"}`}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={s.points.map((p) => ({ t: new Date(p.t).getTime(), v: p.v }))} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.15} />
                                <XAxis dataKey="t" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(t: number) => fmt.time(t)} fontSize={11} />
                                <YAxis width={36} fontSize={11} domain={s.metric.endsWith("Percentage") ? [0, 100] : [0, "auto"]} />
                                <Tooltip labelFormatter={(t) => fmt.when(new Date(Number(t)).toISOString())} formatter={(v) => [String(v), LABELS[s.metric as ConcurrencyMetric] ?? s.metric]} />
                                <Line type="monotone" dataKey="v" stroke="var(--color-accent)" dot={false} strokeWidth={2} isAnimationActive={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <table className="sr-only">
                            <caption>{LABELS[s.metric as ConcurrencyMetric] ?? s.metric} data points</caption>
                            <thead>
                              <tr>
                                <th scope="col">Time</th>
                                <th scope="col">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.points.map((p) => (
                                <tr key={p.t}>
                                  <td>{p.t}</td>
                                  <td>{p.v}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </figure>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
    </div>
  );
}
